import type {DomainCountry} from '@getmoss/moss-analytics'
import {track as trackMossAnalytics} from '@getmoss/moss-analytics'
import {isFilled} from '@prismicio/helpers'
import {PrismicNextImage} from '@prismicio/next/pages'
import {PrismicRichText} from '@prismicio/react'
import Link from 'next/link'
import {useRouter} from 'next/router'
import {useLinkResolver} from 'prismicio'
import type {TestimonialsSectionSliceDefaultPrimaryTestimonialsItem} from 'prismicio-types'
import type {FC} from 'react'
import {EVENTS, getLinkLocale, track} from 'src/common/helpers'
import {Icon} from 'src/common/Icon'
import type {Context} from 'src/common/types'
import {OpenQuote} from './OpenQuote'

type TestimonialContentProps = {
  testimonial: TestimonialsSectionSliceDefaultPrimaryTestimonialsItem
  activeTab: number
  componentIndex: number
  context: Context
  domainCountry: DomainCountry
}

export const TestimonialContent: FC<TestimonialContentProps> = ({
  testimonial,
  activeTab,
  componentIndex,
  context,
  domainCountry,
}) => {
  const {linkResolver} = useLinkResolver()
  const {locale} = useRouter()
  const linkLocale = getLinkLocale(testimonial.link_url, locale)

  return (
    <>
      <div className="flex flex-col gap-3 md:gap-8">
        <OpenQuote />
        <PrismicRichText
          field={testimonial.quote}
          components={{
            paragraph: ({children}) => (
              <p className="text-[22px] leading-[24px] text-moss-white md:max-w-[648px] md:text-[32px] md:leading-[38px]">
                {children}
              </p>
            ),
          }}
        />
        <PrismicRichText
          field={testimonial.author}
          components={{
            paragraph: ({children}) => (
              <p className="text-detail font-semibold uppercase text-moss-white max-md:pt-3 md:text-[13px] md:leading-4 md:tracking-[.52px]">
                {children}
              </p>
            ),
          }}
        />
      </div>
      <div className="flex justify-end md:justify-between">
        <PrismicNextImage
          field={testimonial.company_logo}
          className="max-h-[30px] w-fit max-w-[195px] object-cover max-md:hidden"
        />
        {isFilled.keyText(testimonial.link_label) && (
          <Link
            href={linkResolver(testimonial.link_url)}
            locale={linkLocale}
            className="group relative flex h-[38px] items-center gap-[14px] rounded-[4px] border border-moss-grey-150 bg-moss-white pl-[14px] pr-[7px] hover:bg-moss-grey-100"
            onClick={() => {
              track({
                event: EVENTS.CLICK,
                mktgId: `testimonial-${activeTab}-button`,
                location: `component-${componentIndex}-testimonials`,
                pageName: context.pageUid,
                dataType: 'customer-story-button',
              })

              trackMossAnalytics({
                event: 'interaction',
                interaction: {
                  element: `testimonial-lean-more-button`,
                  location: `component-${componentIndex}-testimonials-${activeTab}`,
                  type: 'click',
                },
                page: {
                  domainCountry,
                  type: context.pageUid || 'unknown',
                },
              })
            }}
          >
            <span className="text-button-s uppercase text-black">{testimonial.link_label}</span>
            <div className="flex size-6 items-center justify-center rounded-sm bg-green-neon group-hover:bg-green-neon/[80%]">
              <Icon name="arrow" className="h-2 w-2.5 text-green-dark" />
            </div>
          </Link>
        )}
      </div>
    </>
  )
}
